import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { USE_MOCK, catalogApi } from '@/api/client';
import { MOCK_FUNDAMENTALS_CATALOG, MOCK_BEHAVIORS } from '@/data/mockCatalog';
import type { FundamentalCatalogItem, DailyHabitCatalogItem, AttitudeBehaviorCatalogItem } from '@/api/types';

/** Palette fallback — used when the backend document has no color field */
const CATALOG_COLOR_PALETTE = [
  '#342F82', '#20B15A', '#F08521', '#E11F26',
  '#40B8E9', '#8B5CF6', '#DC2626', '#EA580C', '#0891B2', '#16A34A',
];

const QUERY_KEY = ['catalog', 'fundamentals'] as const;

export function useFundamentals() {
  return useQuery<FundamentalCatalogItem[]>({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      if (USE_MOCK) {
        return MOCK_FUNDAMENTALS_CATALOG.map((f, idx): FundamentalCatalogItem => ({
          id: f.id,
          code: f.id.slice(0, 3).toUpperCase(),
          order: idx + 1,
          names: { es: f.short, en: f.short },
          dailyHabits: (MOCK_BEHAVIORS[f.id] ?? []).map((b, bi): DailyHabitCatalogItem => ({
            id: b.id,
            fundamentalId: f.id,
            code: `${f.id.slice(0, 3).toUpperCase()}-${String(bi + 1).padStart(3, '0')}`,
            order: bi + 1,
            names: { es: b.name, en: b.name },
            questionIntro: { es: (b as { intro?: string }).intro ?? '', en: (b as { intro?: string }).intro ?? '' },
            isActive: true,
          })),
          isActive: true,
          color: f.color,
          shortName: f.short,
          attitudes: [],
        }));
      }
      const items = await catalogApi.getFundamentals();
      return items.map((f, idx) => ({
        ...f,
        color: f.color ?? CATALOG_COLOR_PALETTE[idx % CATALOG_COLOR_PALETTE.length],
        shortName: f.shortName ?? f.names['en'] ?? f.names['es'] ?? f.id,
        attitudes: f.attitudes ?? [],
      }));
    },
    staleTime: 30 * 60 * 1000, // 30 min — catalog rarely changes
  });
}

/** Get display name in given language with multilanguage fallback */
export function getFundamentalName(f: FundamentalCatalogItem, lang: string): string {
  return f.names[lang] ?? f.names['es'] ?? f.names['en'] ?? f.shortName ?? f.id;
}

/** Get daily habit display name in given language with fallback */
export function getHabitName(h: DailyHabitCatalogItem, lang: string): string {
  return h.names[lang] ?? h.names['es'] ?? h.names['en'] ?? h.id;
}

/** Get attitude/behavior display name in given language with fallback */
export function getAttitudeName(attitude: AttitudeBehaviorCatalogItem, lang: string): string {
  return attitude.names[lang] ?? attitude.names['es'] ?? attitude.names['en'] ?? attitude.id;
}

/** Get attitude/behavior display description in given language with fallback */
export function getAttitudeDescription(attitude: AttitudeBehaviorCatalogItem, lang: string): string {
  return attitude.descriptions?.[lang] ?? attitude.descriptions?.['es'] ?? attitude.descriptions?.['en']
    ?? attitude.description ?? '';
}

// ── Mutations ──────────────────────────────────────────────────────────────────

export function useCreateFundamental() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { names: Record<string, string>; shortNames?: Record<string, string>; shortName?: string; color?: string; descriptions?: Record<string, string>; description?: string; icon?: string; url?: string; micrositeUrl?: string }) =>
      catalogApi.createFundamental(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useUpdateFundamental() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; names: Record<string, string>; shortNames?: Record<string, string>; shortName?: string; color?: string; descriptions?: Record<string, string>; description?: string; icon?: string; url?: string; micrositeUrl?: string }) =>
      catalogApi.updateFundamental(id, body),
    // Bug reportado: cambiar el ícono de un Fundamental y volver a Reconocimiento mostraba el
    // ícono VIEJO 1-2s antes de refrescar. `invalidateQueries` marca la query obsoleta y dispara
    // un refetch en segundo plano, pero cualquier componente ya montado (o que monte antes de que
    // ese refetch resuelva, p. ej. al navegar justo después de guardar) sigue leyendo el `data`
    // cacheado — el viejo — hasta que la respuesta de red vuelve. `setQueryData` escribe la
    // respuesta YA CONOCIDA (el propio Fundamental actualizado que regresa la mutación) de forma
    // sincrónica, sin esperar ese round-trip: la caché queda correcta en el mismo tick en que la
    // mutación resuelve, antes de que el usuario llegue a navegar a ningún lado. `invalidateQueries`
    // se conserva como red de seguridad para resincronizar con el servidor.
    //
    // Guarda defensiva `if (!updated?.id) return`: `request<T>` (client.ts) hace `res.json() as T`
    // SIN validar en runtime que la respuesta calce con el tipo — y devuelve `undefined` de plano
    // en un 204. Si el backend real de este endpoint alguna vez responde 204 o un cuerpo sin `id`,
    // sin esta guarda `updated.id` tronaría el mutation callback entero. Con la guarda, ese caso
    // simplemente se salta el parche sincrónico y se apoya solo en `invalidateQueries` — el mismo
    // comportamiento (más lento, pero correcto) que había antes de este fix.
    onSuccess: (updated) => {
      if (updated?.id) {
        qc.setQueryData<FundamentalCatalogItem[]>(QUERY_KEY, old =>
          old?.map(f => (f.id === updated.id ? { ...f, ...updated } : f)));
      }
      qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

export function useDeleteFundamental() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => catalogApi.deleteFundamental(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useCreateDailyHabit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fundamentalId, names, attitudeBehaviorId }: { fundamentalId: string; names: Record<string, string>; attitudeBehaviorId?: string }) =>
      catalogApi.createDailyHabit(fundamentalId, { names, attitudeBehaviorId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useUpdateDailyHabit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fundamentalId, habitId, names }: { fundamentalId: string; habitId: string; names: Record<string, string> }) =>
      catalogApi.updateDailyHabit(fundamentalId, habitId, { names }),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useDeleteDailyHabit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fundamentalId, habitId }: { fundamentalId: string; habitId: string }) =>
      catalogApi.deleteDailyHabit(fundamentalId, habitId),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useCreateAttitude() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fundamentalId, names, descriptions }: { fundamentalId: string; names: Record<string, string>; descriptions?: Record<string, string> }) =>
      catalogApi.createAttitude(fundamentalId, { names, descriptions }),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useUpdateAttitude() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fundamentalId, attitudeId, names, descriptions }: { fundamentalId: string; attitudeId: string; names: Record<string, string>; descriptions?: Record<string, string> }) =>
      catalogApi.updateAttitude(fundamentalId, attitudeId, { names, descriptions }),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useDeleteAttitude() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fundamentalId, attitudeId }: { fundamentalId: string; attitudeId: string }) =>
      catalogApi.deleteAttitude(fundamentalId, attitudeId),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useMoveFundamental() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, direction }: { id: string; direction: 'up' | 'down' }) =>
      catalogApi.moveFundamental(id, direction),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useMoveAttitude() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fundamentalId, attitudeId, direction }: { fundamentalId: string; attitudeId: string; direction: 'up' | 'down' }) =>
      catalogApi.moveAttitude(fundamentalId, attitudeId, direction),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useMoveDailyHabit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fundamentalId, habitId, direction }: { fundamentalId: string; habitId: string; direction: 'up' | 'down' }) =>
      catalogApi.moveDailyHabit(fundamentalId, habitId, direction),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useUploadFundamentalImage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fundamentalId, file }: { fundamentalId: string; file: File }) =>
      catalogApi.uploadFundamentalImage(fundamentalId, file),
    // Mismo fix y misma guarda defensiva que useUpdateFundamental (ver comentario ahí): escribe
    // la imagen recién subida en la caché de forma sincrónica en vez de solo invalidar, para que
    // no haya un lapso mostrando la imagen anterior mientras se resuelve el refetch en segundo
    // plano — pero solo si la respuesta trae un `id` reconocible.
    onSuccess: (updated) => {
      if (updated?.id) {
        qc.setQueryData<FundamentalCatalogItem[]>(QUERY_KEY, old =>
          old?.map(f => (f.id === updated.id ? { ...f, ...updated } : f)));
      }
      qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

export function useUploadAttitudeImage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fundamentalId, attitudeId, file }: { fundamentalId: string; attitudeId: string; file: File }) =>
      catalogApi.uploadAttitudeImage(fundamentalId, attitudeId, file),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useUploadDailyHabitImage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fundamentalId, habitId, file }: { fundamentalId: string; habitId: string; file: File }) =>
      catalogApi.uploadDailyHabitImage(fundamentalId, habitId, file),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}
